import {
  aiChatGuardianReviewOptions,
  aiChatGuardianReviewsOptions,
  aiChatQueryKeys,
  aiChatThreadOptions,
  aiChatTimelineOptions,
} from "@/lib/data/react-query/ai-chat";
import { chatApi } from "@/lib/data/requests/ai-chat";
import type {
  AiChatGuardianDecision,
  AiChatGuardianReview,
  AiChatItemBodyChunk,
  AiChatItemBodyPart,
  AiChatLoadedItemBody,
  AiChatTimelineItem,
  AiChatThreadDetail,
  AiChatTimelinePage,
  ChatApi,
  AiChatQuestionAnswer,
} from "@/lib/types/ai-chat";
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  applyAiChatStreamEvent,
  uniqueTimelineItems,
  updateAiChatThreadHeaderCaches,
} from "./ai-chat-cache";
import { useChatLayout } from "./useChatLayout";
import { layoutFromItem, type ChatLayoutData } from "./chat-layout";
import { useAiChatSearchReveal } from "./useAiChatSearchReveal";
import { useAiChatStream } from "./useAiChatStream";
import {
  useAiChatMessageQueue,
  type QueuedChatMessage,
} from "./useAiChatMessageQueue";

const BODY_CHUNK_BYTES = 32_768;
const BODY_CLIENT_LIMIT_BYTES = 262_144;
const BODY_CACHE_ENTRY_LIMIT = 20;

export const aiChatBodyKey = (itemId: string) => itemId;

const withBoundedEntry = <T>(
  current: Partial<Record<string, T>>,
  key: string,
  value: T,
) => {
  const entries: Array<[string, T]> = [];
  for (const [existingKey, existingValue] of Object.entries(current)) {
    if (existingKey !== key && existingValue !== undefined) {
      entries.push([existingKey, existingValue]);
    }
  }
  entries.push([key, value]);
  return Object.fromEntries(entries.slice(-BODY_CACHE_ENTRY_LIMIT));
};

export const useAiChatController = ({
  api = chatApi,
  threadId,
  matterId,
  onThreadCreated,
  queueScopeKey,
}: {
  api?: ChatApi;
  queueScopeKey: string;
  threadId?: string;
  matterId?: string;
  onThreadCreated?: (threadId: string) => void;
}) => {
  const queryClient = useQueryClient();
  const [createdThreadId, setCreatedThreadId] = useState<string>();
  const [createdRunId, setCreatedRunId] = useState<string>();
  const [interactionError, setInteractionError] = useState<Error | null>(null);
  const [loadedBodies, setLoadedBodies] = useState<
    Partial<Record<string, AiChatLoadedItemBody>>
  >({});
  const [pendingBodyIds, setPendingBodyIds] = useState<Set<string>>(new Set());
  const [approvalDetails, setApprovalDetails] = useState<
    Record<string, AiChatGuardianReview>
  >({});
  const [decidingReviewIds, setDecidingReviewIds] = useState<Set<string>>(
    new Set(),
  );
  const lastAcknowledgedRevisionRef = useRef(0);
  const createdThreadIdRef = useRef<string>();
  const operationGenerationRef = useRef(0);
  const timelineNavigationRef = useRef(0);
  const windowRequestRef = useRef<AbortController>();
  const [isLoadingWindow, setIsLoadingWindow] = useState(false);
  const bodyRequestSequenceRef = useRef(0);
  const bodyRequestIdsRef = useRef(new Map<string, number>());
  const resolvedThreadId = threadId ?? createdThreadId;

  useLayoutEffect(() => {
    operationGenerationRef.current += 1;
    timelineNavigationRef.current += 1;
    windowRequestRef.current?.abort();
    setIsLoadingWindow(false);
    const isCreatedThreadSelection =
      Boolean(threadId) && threadId === createdThreadIdRef.current;
    createdThreadIdRef.current = undefined;
    setCreatedThreadId(undefined);
    if (isCreatedThreadSelection) return;
    setCreatedRunId(undefined);
    setLoadedBodies({});
    setApprovalDetails({});
    setPendingBodyIds(new Set());
    setDecidingReviewIds(new Set());
    bodyRequestIdsRef.current.clear();
    lastAcknowledgedRevisionRef.current = 0;
    setInteractionError(null);
  }, [threadId]);

  const threadQuery = useQuery({
    ...aiChatThreadOptions(resolvedThreadId, api),
    refetchInterval: (query) =>
      query.state.data?.readOnly && query.state.data.activeRunId
        ? 5_000
        : false,
    refetchIntervalInBackground: false,
  });
  const awaitingAutomationCompletion =
    threadQuery.data?.readOnly === true &&
    !["completed", "completed_unread"].includes(threadQuery.data.thread.status);
  const timelineQuery = useInfiniteQuery({
    ...aiChatTimelineOptions(resolvedThreadId, api),
    enabled: Boolean(
      resolvedThreadId && threadQuery.data && !awaitingAutomationCompletion,
    ),
  });
  const items = useMemo(
    () => uniqueTimelineItems(timelineQuery.data?.pages),
    [timelineQuery.data?.pages],
  );
  const layout = useChatLayout(
    resolvedThreadId,
    api,
    Boolean(threadQuery.data && !awaitingAutomationCompletion),
  );
  useEffect(() => {
    if (!resolvedThreadId || !items.length) return;
    queryClient.setQueryData<ChatLayoutData>(
      aiChatQueryKeys.layout(resolvedThreadId),
      (current) => {
        const latestLoadedRevision = Math.max(
          ...items.map((item) => item.revision),
        );
        if (
          !current ||
          (current.complete && current.revision >= latestLoadedRevision)
        )
          return current;
        return {
          items: items.map(layoutFromItem),
          revision: latestLoadedRevision,
          complete: current.complete,
        };
      },
    );
  }, [items, queryClient, resolvedThreadId]);
  const revision = useMemo(
    () =>
      Math.max(
        ...(timelineQuery.data?.pages.map((page) => page.revision) ?? [0]),
      ),
    [timelineQuery.data?.pages],
  );
  const activeRunId =
    createdRunId ?? threadQuery.data?.activeRunId ?? undefined;

  useEffect(() => {
    const revisions = new Map(items.map((item) => [item.id, item.revision]));
    setLoadedBodies((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(
          ([itemId, body]) =>
            body !== undefined && revisions.get(itemId) === body.revision,
        ),
      );
      return Object.keys(next).length === Object.keys(current).length
        ? current
        : next;
    });
  }, [items]);

  const refreshLatest = useCallback(async () => {
    if (!resolvedThreadId) return;
    try {
      const generation = operationGenerationRef.current;
      const page = await api.getTimeline(resolvedThreadId, { limit: 50 });
      if (generation !== operationGenerationRef.current) return;
      const detail = queryClient.getQueryData<AiChatThreadDetail>(
        aiChatQueryKeys.thread(resolvedThreadId),
      );
      if (detail)
        applyAiChatStreamEvent(queryClient, {
          type: "thread.snapshot",
          threadId: resolvedThreadId,
          thread: detail.thread,
          items: page.items,
          beforeCursor: page.beforeCursor,
          afterCursor: page.afterCursor,
          hasMoreBefore: page.hasMoreBefore,
          hasMoreAfter: page.hasMoreAfter,
          cursor: String(page.revision),
          resyncRequired: false,
        });
    } catch (error) {
      setInteractionError(
        error instanceof Error
          ? error
          : new Error("The latest conversation could not be loaded."),
      );
    }
  }, [api, queryClient, resolvedThreadId]);
  const handleStreamClosed = useCallback(() => {
    setCreatedRunId(undefined);
  }, []);
  const stream = useAiChatStream({
    api,
    runId: threadQuery.data?.readOnly ? undefined : activeRunId,
    threadId: resolvedThreadId,
    refreshLatest,
    onClosed: handleStreamClosed,
  });

  const confirmedRequestIds = useMemo(
    () =>
      items.flatMap((item) =>
        item.kind === "user_message" &&
        typeof item.payload.clientRequestId === "string"
          ? [item.payload.clientRequestId]
          : [],
      ),
    [items],
  );

  const messageQueue = useAiChatMessageQueue({
    confirmedRequestIds,
    scopeKey: queueScopeKey,
    threadId: resolvedThreadId,
    api,
    matterId,
    onDelivered: ({ conversationId, runId }) => {
      if (conversationId !== resolvedThreadId) {
        createdThreadIdRef.current = conversationId;
        setCreatedThreadId(conversationId);
        onThreadCreated?.(conversationId);
      }
      if (runId) setCreatedRunId(runId);
      void queryClient.invalidateQueries({
        queryKey: aiChatQueryKeys.threadLists(),
      });
      void queryClient.invalidateQueries({
        queryKey: aiChatQueryKeys.thread(conversationId),
      });
      void queryClient.invalidateQueries({
        queryKey: aiChatQueryKeys.timeline(conversationId),
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!activeRunId || threadQuery.data?.readOnly) return undefined;
      const generation = operationGenerationRef.current;
      const run = await api.cancelRun(activeRunId);
      return { run, generation, threadId: resolvedThreadId };
    },
    onSuccess: async (result) => {
      if (
        !result ||
        result.generation !== operationGenerationRef.current ||
        !result.threadId
      )
        return;
      if (["completed", "failed", "cancelled"].includes(result.run.status)) {
        setCreatedRunId((current) =>
          current === result.run.id ? undefined : current,
        );
        queryClient.setQueryData<AiChatThreadDetail>(
          aiChatQueryKeys.thread(result.threadId),
          (current) =>
            current?.activeRunId === result.run.id
              ? { ...current, activeRunId: null }
              : current,
        );
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: aiChatQueryKeys.threadLists(),
          }),
          queryClient.invalidateQueries({
            queryKey: aiChatQueryKeys.thread(result.threadId),
          }),
          queryClient.invalidateQueries({
            queryKey: aiChatQueryKeys.timeline(result.threadId),
          }),
        ]);
      }
    },
  });

  const loadItemBodyResult = useCallback(
    async (
      item: AiChatTimelineItem,
      part: AiChatItemBodyPart,
      options?: { throughClientLimit?: boolean; signal?: AbortSignal },
    ) => {
      const generation = operationGenerationRef.current;
      const requestThreadId = resolvedThreadId;
      if (!requestThreadId) return;
      const key = aiChatBodyKey(item.id);
      const current: AiChatLoadedItemBody | undefined = loadedBodies[key];
      const matchesCurrentPart =
        current?.revision === item.revision && current.part === part;
      const currentText = matchesCurrentPart ? current.text : "";
      const currentBytes = new TextEncoder().encode(currentText).byteLength;
      if (
        (matchesCurrentPart && current.isComplete) ||
        currentBytes >= BODY_CLIENT_LIMIT_BYTES
      ) {
        return matchesCurrentPart ? current : undefined;
      }
      const bodyRequestId = ++bodyRequestSequenceRef.current;
      bodyRequestIdsRef.current.set(key, bodyRequestId);
      const requestIsCurrent = () =>
        generation === operationGenerationRef.current &&
        bodyRequestIdsRef.current.get(key) === bodyRequestId &&
        !options?.signal?.aborted;
      setPendingBodyIds((ids) => new Set(ids).add(key));
      setInteractionError(null);
      try {
        let text = currentText;
        let loadedBytes = currentBytes;
        let requestOffset = currentBytes;
        let nextOffset: number | null = currentBytes;
        let latestChunk: AiChatItemBodyChunk;
        do {
          if (!requestIsCurrent()) return;
          const chunk = await api.getItemBody(requestThreadId, item.id, {
            revision: item.revision,
            part,
            offset: requestOffset,
            limitBytes: Math.min(
              BODY_CHUNK_BYTES,
              BODY_CLIENT_LIMIT_BYTES - loadedBytes,
            ),
            signal: options?.signal,
          });
          if (!requestIsCurrent()) return;
          text =
            requestOffset === chunk.offset ? text + chunk.text : chunk.text;
          loadedBytes = new TextEncoder().encode(text).byteLength;
          nextOffset = chunk.nextOffset;
          latestChunk = chunk;
          if (nextOffset !== null) requestOffset = nextOffset;
        } while (
          options?.throughClientLimit &&
          nextOffset !== null &&
          loadedBytes < BODY_CLIENT_LIMIT_BYTES
        );
        if (!requestIsCurrent()) return;
        const body: AiChatLoadedItemBody = {
          itemId: latestChunk.itemId,
          revision: latestChunk.revision,
          part: latestChunk.part,
          availableParts: latestChunk.availableParts,
          text,
          totalBytes: latestChunk.totalBytes,
          contentType: latestChunk.contentType,
          isComplete: nextOffset === null,
          reachedClientLimit:
            nextOffset !== null && loadedBytes >= BODY_CLIENT_LIMIT_BYTES,
        };
        setLoadedBodies((bodies) => withBoundedEntry(bodies, key, body));
        return body;
      } catch (error) {
        if (!requestIsCurrent()) return;
        if (isAxiosError(error) && error.response?.status === 409) {
          setLoadedBodies((bodies) =>
            Object.fromEntries(
              Object.entries(bodies).filter(([itemId]) => itemId !== key),
            ),
          );
          setInteractionError(
            new Error("This item changed. Its latest version has been loaded."),
          );
          await refreshLatest();
          return;
        }
        const loadError =
          error instanceof Error
            ? error
            : new Error("The item details could not be loaded.");
        setInteractionError(loadError);
        throw loadError;
      } finally {
        if (
          generation === operationGenerationRef.current &&
          bodyRequestIdsRef.current.get(key) === bodyRequestId
        ) {
          bodyRequestIdsRef.current.delete(key);
          setPendingBodyIds((ids) => {
            const next = new Set(ids);
            next.delete(key);
            return next;
          });
        }
      }
    },
    [api, loadedBodies, refreshLatest, resolvedThreadId],
  );

  const loadItemBody = useCallback(
    async (item: AiChatTimelineItem, part: AiChatItemBodyPart) => {
      await loadItemBodyResult(item, part);
    },
    [loadItemBodyResult],
  );

  const beginTimelineNavigation = useCallback(() => {
    timelineNavigationRef.current += 1;
    windowRequestRef.current?.abort();
  }, []);
  useEffect(() => () => windowRequestRef.current?.abort(), []);
  const searchReveal = useAiChatSearchReveal({
    api,
    onBeforeNavigation: beginTimelineNavigation,
    threadId:
      threadQuery.data && !awaitingAutomationCompletion
        ? resolvedThreadId
        : undefined,
    items,
    loadItemBody: loadItemBodyResult,
    setInteractionError,
  });

  const loadTimelineWindow = useCallback(
    async (itemId: string) => {
      if (!resolvedThreadId) return;
      windowRequestRef.current?.abort();
      const controller = new AbortController();
      windowRequestRef.current = controller;
      const generation = operationGenerationRef.current;
      const navigation = timelineNavigationRef.current;
      const isCurrentWindow = () =>
        !controller.signal.aborted &&
        generation === operationGenerationRef.current &&
        navigation === timelineNavigationRef.current;
      setIsLoadingWindow(true);
      try {
        const page = await api.getTimeline(resolvedThreadId, {
          aroundItemId: itemId,
          limit: 100,
          signal: controller.signal,
        });
        if (!isCurrentWindow()) return;
        const key = aiChatQueryKeys.timeline(resolvedThreadId);
        await queryClient.cancelQueries({ queryKey: key, exact: true });
        if (!isCurrentWindow()) return;
        queryClient.setQueryData<InfiniteData<AiChatTimelinePage>>(key, {
          pages: [page],
          pageParams: [{ direction: "around", itemId }],
        });
      } catch (error) {
        if (!controller.signal.aborted) throw error;
      } finally {
        if (windowRequestRef.current === controller) setIsLoadingWindow(false);
      }
    },
    [api, queryClient, resolvedThreadId],
  );

  const { cancelTimelineReveal } = searchReveal;
  const jumpToLatest = useCallback(async () => {
    if (!resolvedThreadId) return false;
    const navigation = ++timelineNavigationRef.current;
    const generation = operationGenerationRef.current;
    windowRequestRef.current?.abort();
    cancelTimelineReveal();
    const key = aiChatQueryKeys.timeline(resolvedThreadId);
    await queryClient.cancelQueries({ queryKey: key, exact: true });
    const page = await api.getTimeline(resolvedThreadId, { limit: 100 });
    if (
      generation !== operationGenerationRef.current ||
      navigation !== timelineNavigationRef.current
    )
      return false;
    const current = uniqueTimelineItems(
      queryClient.getQueryData<InfiniteData<AiChatTimelinePage>>(key)?.pages,
    );
    const latest = current.filter(
      (item) => item.sequence > (page.items.at(-1)?.sequence ?? 0),
    );
    const merged = uniqueTimelineItems([
      { ...page, items: [...page.items, ...latest] },
    ]);
    queryClient.setQueryData<InfiniteData<AiChatTimelinePage>>(key, {
      pages: [{ ...page, items: merged }],
      pageParams: [{ direction: "initial" }],
    });
    return true;
  }, [api, queryClient, resolvedThreadId, cancelTimelineReveal]);

  const loadApprovalDetail = useCallback(
    async (runId: string, reviewId: string) => {
      const generation = operationGenerationRef.current;
      setInteractionError(null);
      try {
        const review = await queryClient.fetchQuery(
          aiChatGuardianReviewOptions(runId, reviewId, api),
        );
        if (generation !== operationGenerationRef.current) return;
        setApprovalDetails((reviews) =>
          withBoundedEntry(reviews, reviewId, review),
        );
      } catch (error) {
        if (generation === operationGenerationRef.current) {
          setInteractionError(
            error instanceof Error
              ? error
              : new Error("The approval details could not be loaded."),
          );
        }
      }
    },
    [api, queryClient],
  );

  const decideApproval = useCallback(
    async (
      runId: string,
      reviewId: string,
      decision: AiChatGuardianDecision,
    ) => {
      const generation = operationGenerationRef.current;
      setDecidingReviewIds((ids) => new Set(ids).add(reviewId));
      setInteractionError(null);
      try {
        const review = await api.decideGuardianReview(
          runId,
          reviewId,
          decision,
        );
        if (generation !== operationGenerationRef.current) return;
        setApprovalDetails((reviews) =>
          withBoundedEntry(reviews, reviewId, review),
        );
        if (resolvedThreadId) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: aiChatQueryKeys.thread(resolvedThreadId),
              exact: true,
            }),
            queryClient.invalidateQueries({
              queryKey: aiChatQueryKeys.timeline(resolvedThreadId),
              exact: true,
            }),
          ]);
        }
        await queryClient.invalidateQueries({
          queryKey: aiChatQueryKeys.reviews(runId, "awaiting_human"),
        });
        await queryClient.invalidateQueries({
          queryKey: aiChatQueryKeys.threadLists(),
        });
      } catch (error) {
        if (generation === operationGenerationRef.current) {
          setInteractionError(
            error instanceof Error
              ? error
              : new Error("The approval decision could not be saved."),
          );
        }
      } finally {
        if (generation === operationGenerationRef.current) {
          setDecidingReviewIds((ids) => {
            const next = new Set(ids);
            next.delete(reviewId);
            return next;
          });
        }
      }
    },
    [api, queryClient, resolvedThreadId],
  );

  const { mutateAsync: markThreadRead } = useMutation({
    mutationFn: async ({
      threadId: receiptThreadId,
      visibleRevision,
      generation,
    }: {
      threadId: string;
      visibleRevision: number;
      generation: number;
    }) => {
      if (generation !== operationGenerationRef.current) return undefined;
      return api.markThreadRead(receiptThreadId, visibleRevision);
    },
    retry: (failureCount, error) =>
      failureCount < 2 &&
      isAxiosError(error) &&
      (!error.response || error.response.status >= 500),
    retryDelay: (attempt) => 1000 * (attempt + 1),
  });

  const ackVisibleRevision = useCallback(
    async (visibleRevision: number) => {
      if (
        !resolvedThreadId ||
        !Number.isFinite(visibleRevision) ||
        document.visibilityState !== "visible" ||
        visibleRevision < revision ||
        visibleRevision <= lastAcknowledgedRevisionRef.current
      ) {
        return;
      }
      const generation = operationGenerationRef.current;
      lastAcknowledgedRevisionRef.current = visibleRevision;
      try {
        const receipt = await markThreadRead({
          threadId: resolvedThreadId,
          visibleRevision,
          generation,
        });
        if (
          !receipt ||
          generation !== operationGenerationRef.current ||
          lastAcknowledgedRevisionRef.current !== visibleRevision
        )
          return;
        const currentThread = queryClient.getQueryData<AiChatThreadDetail>(
          aiChatQueryKeys.thread(resolvedThreadId),
        );
        const currentTimeline = queryClient.getQueryData<
          InfiniteData<AiChatTimelinePage>
        >(aiChatQueryKeys.timeline(resolvedThreadId));
        const currentRevision = Math.max(
          currentThread?.revision ?? 0,
          ...(currentTimeline?.pages.map((page) => page.revision) ?? [0]),
        );
        if (receipt.revision >= currentRevision) {
          updateAiChatThreadHeaderCaches(queryClient, receipt.thread);
        }
      } catch {
        // Read receipts are background bookkeeping, not a failed conversation.
        // A late failure must not undo a newer acknowledgement.
        if (
          generation === operationGenerationRef.current &&
          lastAcknowledgedRevisionRef.current === visibleRevision
        ) {
          lastAcknowledgedRevisionRef.current = Math.max(
            0,
            visibleRevision - 1,
          );
        }
      }
    },
    [markThreadRead, queryClient, resolvedThreadId, revision],
  );

  const { fetchPreviousPage, fetchNextPage } = timelineQuery;
  const { refetch: refetchThread } = threadQuery;
  const { restart } = stream;
  const { enqueue } = messageQueue;
  const { mutateAsync: cancelRunAsync } = cancelMutation;
  const readOnly = threadQuery.data?.readOnly;
  const loadOlder = useCallback(async () => {
    const result = await fetchPreviousPage({
      cancelRefetch: false,
    });
    if (result.isError) throw result.error;
  }, [fetchPreviousPage]);
  const loadNewer = useCallback(async () => {
    const result = await fetchNextPage({ cancelRefetch: false });
    if (result.isError) throw result.error;
  }, [fetchNextPage]);
  const retryConnection = useCallback(async () => {
    setInteractionError(null);
    restart();
    await Promise.all([refetchThread(), refreshLatest()]);
  }, [restart, refetchThread, refreshLatest]);
  const answerQuestion = useCallback(
    async (
      runId: string,
      requestId: string,
      answers: AiChatQuestionAnswer[],
    ) => {
      const result = await api.answerQuestion(runId, requestId, answers);
      if (resolvedThreadId)
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: aiChatQueryKeys.thread(resolvedThreadId),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: aiChatQueryKeys.timeline(resolvedThreadId),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: aiChatQueryKeys.threadLists(),
          }),
        ]);
      return result;
    },
    [api, resolvedThreadId, queryClient],
  );
  const sendMessage = useCallback(
    async (
      userMessage: string,
      fileContentIds?: string[],
      fileReferences?: QueuedChatMessage["fileReferences"],
    ) => {
      if (readOnly)
        throw new Error(
          "This automation conversation is read-only. You can still respond to review requests.",
        );
      await enqueue({
        userMessage,
        fileContentIds,
        fileReferences,
      });
    },
    [readOnly, enqueue],
  );
  const cancelRun = useCallback(async () => {
    await cancelRunAsync();
  }, [cancelRunAsync]);

  return {
    thread: threadQuery.data?.thread,
    readOnly: threadQuery.data?.readOnly === true,
    awaitingAutomationCompletion,
    items: awaitingAutomationCompletion ? [] : items,
    layoutItems: layout.data?.complete ? layout.data.items : undefined,
    loadTimelineWindow,
    revision,
    activeRunId,
    streamStatus: stream.status,
    isLoading: threadQuery.isLoading || timelineQuery.isLoading,
    error:
      interactionError ??
      stream.error ??
      threadQuery.error ??
      timelineQuery.error ??
      layout.error,
    hasOlder: timelineQuery.hasPreviousPage,
    isLoadingOlder: timelineQuery.isFetchingPreviousPage,
    isFetchingTimeline: timelineQuery.isFetching || isLoadingWindow,
    hasNewer: layout.data?.complete ? false : timelineQuery.hasNextPage,
    isLoadingNewer: timelineQuery.isFetchingNextPage,
    loadOlder,
    loadNewer,
    jumpToLatest,
    retryConnection,
    loadedBodies,
    pendingBodyIds,
    loadItemBody,
    ...searchReveal,
    approvalDetails,
    decidingReviewIds,
    loadApprovalDetail,
    answerQuestion,
    decideApproval,
    sendMessage,
    queuedMessages: messageQueue.queuedMessages,
    retryMessage: messageQueue.retry,
    removeQueuedMessage: messageQueue.remove,
    cancelRun,
    ackVisibleRevision,
    isSending: false,
    isCancelling: cancelMutation.isPending,
  };
};

export const useAiChatPendingReviews = (
  runId: string | undefined,
  api: ChatApi = chatApi,
) =>
  useInfiniteQuery(aiChatGuardianReviewsOptions(runId, "awaiting_human", api));
